import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "template", "container", "item", "netTotal", "vatTotal", "grandTotal", "tableWrapper", "emptyState"
  ]

  connect() {
    this.updateRowNumbers()
  }

  add(event) {
    event.preventDefault()

    // Get template content
    const content = this.templateTarget.content.cloneNode(true)

    // Get the next available index
    const nextIndex = this.getNextIndex()
    const newContent = content.querySelector('*').outerHTML.replace(/NEW_RECORD/g, nextIndex)

    // Show table if it was hidden (empty state)
    if (this.hasTableWrapperTarget) this.tableWrapperTarget.classList.remove('hidden')
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add('hidden')

    // Add the new item to the container
    this.containerTarget.insertAdjacentHTML('beforeend', newContent)
    document.dispatchEvent(new CustomEvent("default-memory:dynamic-row-added", {
      detail: {
        association: "invoice_line_items_attributes",
        fromIndex: "NEW_RECORD",
        toIndex: nextIndex
      }
    }))
    this.updateRowNumbers()
  }

  remove(event) {
    event.preventDefault()
    const lineItem = event.currentTarget.closest('.line-item')
    lineItem.style.display = 'none'
    // Mark for deletion when form is submitted
    const destroyInput = lineItem.querySelector('input[name*="_destroy"]')
    if (destroyInput) destroyInput.value = '1'
    this.updateRowNumbers()
  }

  getNextIndex() {
    // Get all existing line item inputs
    const inputs = document.querySelectorAll('input[name*="[invoice_line_items_attributes]"]')
    let maxIndex = -1

    // Find the highest index currently in use
    inputs.forEach(input => {
      const match = input.name.match(/\[invoice_line_items_attributes\]\[(\d+)\]/)
      if (match) {
        const index = parseInt(match[1])
        maxIndex = Math.max(maxIndex, index)
      }
    })

    return maxIndex + 1
  }

  updateRowNumbers() {
    this.visibleRows().forEach((row, index) => {
      const rowNumberEl = row.querySelector('[data-line-item-target="rowNumber"]')
      if (rowNumberEl) {
        rowNumberEl.textContent = index + 1
      }
    })
    this.updateTotals()
  }

  updateTotals(event) {
    if (event && !event.target.matches(
      'input[name*="[net_amount]"], input[name*="[vat_amount]"], input[name*="[total_amount]"]'
    )) return

    const totals = this.visibleRows().reduce((sum, row) => {
      sum.net += this.centsValue(row, "net_amount")
      sum.vat += this.centsValue(row, "vat_amount")
      sum.grand += this.centsValue(row, "total_amount")
      return sum
    }, { net: 0, vat: 0, grand: 0 })

    if (this.hasNetTotalTarget) this.netTotalTarget.textContent = this.formatAmount(totals.net)
    if (this.hasVatTotalTarget) this.vatTotalTarget.textContent = this.formatAmount(totals.vat)
    if (this.hasGrandTotalTarget) this.grandTotalTarget.textContent = this.formatAmount(totals.grand)
  }

  centsValue(row, field) {
    const value = row.querySelector(`input[name*="[${field}]"]`)?.value
    const decimal = Number.parseFloat(value)
    return Number.isFinite(decimal) ? Math.round(decimal * 100) : 0
  }

  formatAmount(cents) {
    if (cents === 0) return "0.00"

    return (cents / 100).toFixed(2)
  }

  visibleRows() {
    return Array.from(this.containerTarget.querySelectorAll(".line-item")).filter((row) => {
      const destroyInput = row.querySelector('input[name*="[_destroy]"]')
      return row.style.display !== "none" && destroyInput?.value !== "1"
    })
  }
}
